import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Customer, EntityMeta, Staff, Vendor } from '@qtek/shared/models';
import { Observable, of, pipe } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { StaffService } from './staff.service';

export interface RelationsStoreState {
  customers: Customer[];
  staff: Staff[];
  isStaffLoading: boolean;
  vendors: Vendor[];
  staffMeta: EntityMeta | null;
}

const initialState: RelationsStoreState = {
  customers: [],
  staff: [],
  isStaffLoading: false,
  vendors: [],
  staffMeta: null,
};

export const RelationsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, service = inject(StaffService)) => ({
    getStaff: rxMethod<{ id?: string | number; query?: unknown }>(
      pipe(
        tap(() => patchState(store, { isStaffLoading: true })),
        switchMap(({ id, query }) =>
          service.getStaff(id, query).pipe(
            tap(({ res, sts }) =>
              patchState(store, {
                staff: res || [],
                isStaffLoading: false,
                staffMeta: sts?.meta || null,
              })
            ),
            catchError(() => {
              patchState(store, { isStaffLoading: false });
              return of(null);
            })
          )
        )
      )
    ),

    /** Emits the created staff member on success so callers can close their dialog. */
    createEmployee(payload: Staff): Observable<Staff> {
      return service.createEmployee(payload).pipe(
        map(({ res }) => res),
        tap(res =>
          patchState(store, state => ({ staff: [res, ...state.staff] }))
        )
      );
    },

    /** Emits the updated staff member on success; re-throws on failure. */
    updateEmployee(form: Staff, usrId: string): Observable<Staff> {
      return service.updateStaff(form, usrId).pipe(
        map(({ res }) => res),
        tap(res =>
          patchState(store, state => {
            const staff = state.staff.slice();
            const index = staff.findIndex(
              relation => relation.prsId === res.prsId
            );
            if (index !== -1) {
              staff[index] = res;
            }
            return { staff };
          })
        )
      );
    },

    deleteEmployee(id: string): Observable<unknown> {
      return service.deleteStaff(id).pipe(
        tap(() =>
          patchState(store, state => ({
            staff: state.staff.filter(relation => relation._id !== id),
          }))
        )
      );
    },

    updateStaffPhone(payload: {
      userId: string;
      phoneNumber: string;
      car?: string;
      tp: number;
    }): Observable<unknown> {
      return service.updateStaffPhone(payload);
    },

    sendStaffInvitation(prsId: string, query: unknown): Observable<unknown> {
      return service.sendInvitation(prsId, query);
    },
  }))
);
